import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";

import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Turnstile } from "@/components/forms/Turnstile";
import { QQ_HEARD_OPTIONS, rememberLeadId, rememberPhone, trackLeadEvent } from "@/components/forms/quick-quote-shared";
import { startQuickQuote } from "@/lib/forms/quick-quote.functions";
import { rememberW2lPreview } from "@/lib/forms/test-preview";
import { getTrackingParams } from "@/lib/utm-tracking";
import stepIcon from "@/assets/restore/icon-step-1.webp";

const TITLE = "Get a Quick Quote for Women - RESTORE Hair";
const DESCRIPTION =
  "Clinically proven hair restoration for women. Get a personalized quick quote from RESTORE Hair and discover how affordable getting your hair back can be.";
const URL = "https://restorehair.com/quick-quote-1-female/";

export const Route = createFileRoute("/quick-quote-1-female")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, follow" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: QuickQuoteFemale,
});

function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="border border-border bg-background px-4 pb-2 pt-2.5">
      <label
        htmlFor={id}
        className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

const controlCls =
  "w-full border-0 bg-transparent p-0 pt-1.5 pb-1 text-base text-foreground outline-none focus:ring-0";

function Consent({ id, children }: { id: string; children: ReactNode }) {
  return (
    <label className="flex gap-3" htmlFor={id}>
      <input id={id} name={id} type="checkbox" required className="mt-1 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </label>
  );
}

/** Ludwig scale value chosen elsewhere (?ludwig= or session storage). */
const LUDWIG_KEY = "restore_quick_quote_ludwig";

function QuickQuoteFemale() {
  const navigate = useNavigate();
  const start = useServerFn(startQuickQuote);
  const [token, setToken] = useState<string | null>(null);
  const [ludwig, setLudwig] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("ludwig");
    if (fromUrl) {
      setLudwig(fromUrl);
      return;
    }
    try {
      const stored = window.sessionStorage.getItem(LUDWIG_KEY);
      if (stored) setLudwig(stored);
    } catch {
      /* ignore */
    }
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token || submitting) return;
    const fd = new FormData(e.currentTarget);
    setSubmitting(true);
    setError(null);
    try {
      const res = await start({
        data: {
          turnstile_token: token,
          first_name: String(fd.get("first_name") || "").trim(),
          last_name: String(fd.get("last_name") || "").trim(),
          email: String(fd.get("email") || "").trim(),
          phone: String(fd.get("phone") || "").trim(),
          zip: String(fd.get("zip") || "").trim(),
          ludwig: ludwig || undefined,
          heard_about: String(fd.get("heard") || "").trim() || undefined,
          consent_contact: true,
          consent_terms: true,
          consent_sensitive: true,
          page_path: window.location.pathname,
          referrer: document.referrer || undefined,
          tracking: getTrackingParams(),
        },
      });
      rememberW2lPreview(res);
      rememberLeadId(res.lead_id);
      rememberPhone(String(fd.get("phone") || "").trim());
      await trackLeadEvent({
        em: String(fd.get("email") || "").trim(),
        ph: String(fd.get("phone") || "").trim(),
        fn: String(fd.get("first_name") || "").trim(),
        ln: String(fd.get("last_name") || "").trim(),
        zp: String(fd.get("zip") || "").trim(),
      });
      try {
        window.sessionStorage.removeItem(LUDWIG_KEY);
      } catch {
        /* ignore */
      }
      await navigate({ to: "/quick-quote-page-2", search: { qq: res.lead_id } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-10 lg:px-8 md:py-16">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left — brand column */}
          <div>
            <img
              src={stepIcon}
              alt=""
              width={56}
              height={56}
              loading="eager"
              className="h-14 w-14 object-contain"
            />
            <p className="mt-4 text-sm text-muted-foreground">RESTORE® Hair</p>
            <h1 className="mt-6 text-4xl font-light leading-tight tracking-tight text-foreground md:text-5xl">
              Get a Quick <span className="font-extrabold">Quote!</span>
            </h1>
            <p className="mt-6 text-base leading-relaxed text-foreground">
              Get your hair back with the clinically proven solution for women. Independent
              studies show more than 80% of participants saw:
            </p>
            <ul className="mt-6 space-y-3 text-base text-foreground">
              {[
                "Less hair loss within 90 days",
                "Thicker diameter at base of hair within 90 days",
                "A 30% increase in new hair growth within 180 days*",
              ].map((item) => (
                <li key={item} className="flex gap-3">
                  <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 bg-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — form column */}
          <div>
            <div className="flex gap-3" aria-label="Step 1 of 2">
              <span className="h-1 flex-1 bg-foreground" />
              <span className="h-1 flex-1 bg-border" />
            </div>

            <h2 className="mt-8 text-2xl font-light text-foreground md:text-3xl">
              Tell Us About Yourself
            </h2>

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field id="first_name" label="First Name *">
                  <input className={controlCls} id="first_name" name="first_name" required maxLength={80} autoComplete="given-name" />
                </Field>
                <Field id="last_name" label="Last Name *">
                  <input className={controlCls} id="last_name" name="last_name" required maxLength={80} autoComplete="family-name" />
                </Field>
              </div>
              <Field id="phone" label="Phone *">
                <input className={controlCls} id="phone" name="phone" type="tel" required maxLength={32} autoComplete="tel" />
              </Field>
              <Field id="email" label="Email *">
                <input className={controlCls} id="email" name="email" type="email" required maxLength={255} autoComplete="email" />
              </Field>
              <Field id="zip" label="Zip Code *">
                <input className={controlCls} id="zip" name="zip" required maxLength={20} autoComplete="postal-code" />
              </Field>
              <Field id="heard" label="How did you hear about RESTORE?">
                <select className={controlCls} id="heard" name="heard" defaultValue="">
                  <option value="">Select one</option>
                  {QQ_HEARD_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </Field>

              {/* Ludwig scale carried over from a picker on another page */}
              <input type="hidden" name="ludwig" value={ludwig} />

              <div className="space-y-5 pt-2 text-xs leading-relaxed text-foreground">
                <Consent id="consent_contact">
                  By checking this box and clicking the Get a Quick Quote button below, I consent
                  to receive automated marketing, advertising, transactional, or informational
                  phone calls, text messages, or emails, potentially involving use of an
                  autodialer or an artificial or prerecorded voice, from or on behalf of Restore
                  Holdings LLC (“Restore”) at the phone number or email address I provided above.
                  I understand my consent is not a condition of purchase. Reply “STOP” to opt-out
                  of text messages.
                </Consent>
                <Consent id="consent_terms">
                  By checking this box and clicking the Get a Quick Quote button below, I also
                  agree to abide by Restore’s{" "}
                  <a className="text-primary underline" href="/terms-of-use/">Terms of Use</a> and{" "}
                  <a className="text-primary underline" href="/privacy-policy/">Privacy Policy</a>,
                  including submitting all disputes to binding individual arbitration.
                </Consent>
                <Consent id="consent_sensitive">
                  <span className="uppercase">
                    By checking this box and clicking the Get a Quick Quote button below, I consent
                    to Restore’s collection and processing of your “sensitive personal information”
                    for the purposes described in our{" "}
                    <a className="text-primary underline" href="/privacy-policy/">Privacy Policy</a>.
                  </span>
                </Consent>
                <p className="uppercase">
                  California Residents: See our{" "}
                  <a className="text-primary underline" href="/privacy-policy/#ccpa-notice">
                    CCPA notice at collection
                  </a>
                </p>
              </div>

              <div className="pt-2">
                <Turnstile
                  action="quick-quote-female"
                  onToken={setToken}
                  onExpired={() => setToken(null)}
                  onError={() => setToken(null)}
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <button
                type="submit"
                disabled={!token || submitting}
                className="w-full bg-primary px-6 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Get A Quick Quote"}
              </button>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
